import React from "react";
import classNames from "classnames";
import Spinner from "~/common/icons/Spinner";
import { SearchBar } from "~/common/components/card/search/SearchBar";
import { CardListItem } from "~/common/api/page";
import { CardItem } from "./CardItem";
import { type CardPostItem } from "~/common/api/post";
import { getCategoryLabel } from "~/utils/card";

/**
 * this front end script is responsible for controlling the "load more" button behavior
 * and the search functionality for the card list
 */
export function LoadMoreContainer({
  firstCardFullWidth,
  pageSize,
  searchBar,
  items,
  loaded,
}: {
  firstCardFullWidth: boolean;
  pageSize: number;
  searchBar?: boolean;
  items: CardListItem[] | CardPostItem[];
  loaded: boolean;
}) {
  // console.log("items", items);
  const elementCount = items.length;

  const [displayCount, setDisplayCount] = React.useState(pageSize);
  const [searchVisible, setSearchVisible] = React.useState<number>();
  const [visibleCards, setVisibleCards] = React.useState<
    CardListItem[] | CardPostItem[]
  >([]);

  React.useEffect(() => {
    setVisibleCards(items.slice(0, pageSize));
  }, [items, pageSize]);

  const handleSearch = (value: string) => {
    if (value === "") {
      setVisibleCards(items.slice(0, displayCount));
      setSearchVisible(undefined);
      return;
    }

    console.log("Searching for:", value);
    const filteredCards = items.filter(
      (item) =>
        item.title.toString().toLowerCase().includes(value.toLowerCase()) ||
        item.description
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase()) ||
        item.searchTags?.some((tag) =>
          tag.toLowerCase().includes(value.toLowerCase()),
        ),
    );
    setVisibleCards(filteredCards);
    setSearchVisible(filteredCards.length);
  };

  // first card width can change based on the firstCardFullWidth prop
  // or if there are  5 cards visible
  const getCardWidth = (
    index: number,
    length: number,
    firstCardFullWidth: boolean,
  ) => {
    if (index == 0) {
      if (firstCardFullWidth) {
        return "full";
      } else if (length === 5) {
        return "double";
      }
    }
    return "single";
  };

  if (!loaded && items.length === 0) {
    return (
      <span className="tw:flex tw:flex-row tw:justify-center tw:items-center">
        <Spinner />
      </span>
    );
  }

  return (
    <div className="cn-container-class tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-4 tw:gap-4 tw:w-full! tw:max-w-full!">
      {searchBar && <SearchBar label="Search..." onSearch={handleSearch} />}
      {searchVisible !== undefined && searchVisible === 0 && (
        <div className="tw:text-md">
          Unfortunately, no lists met your search criteria. Please search again
          with new keywords.
        </div>
      )}
      <div
        id="refGrid"
        className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:w-full"
      >
        {visibleCards.map((item, index) => (
          <CardItem
            key={index}
            title={item.title}
            description={item.description}
            link={item.link}
            image={item.image}
            focalPoint={item.focalPoint}
            label={getCategoryLabel(item)}
            width={getCardWidth(index, visibleCards.length, firstCardFullWidth)}
          />
        ))}
      </div>

      <button
        onClick={() => {
          setVisibleCards(items.slice(0, displayCount + pageSize));
          setDisplayCount(displayCount + pageSize);
        }}
        className={classNames(
          "tw:bg-[#49516f]! tw:text-white! tw:!text-md tw:py-3! tw:!px-5 tw:!rounded-lg tw:!border-0",
          displayCount < elementCount && !searchVisible
            ? "tw:block"
            : "tw:hidden",
        )}
      >
        Load More
      </button>
    </div>
  );
}
